import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import type {MediaSize} from "@khanacademy/wonder-blocks-layout";
import {semanticColor} from "@khanacademy/wonder-blocks-tokens";
import {isGuideChatDisabled, useResetConversation} from "@khan/guide-core";
import {useGuideState} from "@khan/guide-state";
import {downloadConversation} from "@khan/guide-utils";
import {useUserAttributes} from "@khan/shared-core";
import {useLocation} from "@khan/urls";
import {useMediaQuery} from "@khan/util-media-query";

import {ConversationRecordedNotice} from "../conversation-recorded-notice.tsx";
// eslint-disable-next-line no-restricted-imports -- porting from old code.
import CoeditorDrawer from "../deprecated-guide-coeditor/coeditor-drawer.tsx";
import GuideDisclaimer from "../guide-disclaimer.tsx";
import {Guide} from "../guide.tsx";
import {Header} from "../header/header.tsx";
import {HeaderType} from "../header/types.ts";

export interface Props {
    mediaSize: MediaSize;
    isCoeditorOpen: boolean;
    onClose: () => void;
    showMenu: boolean;
    onToggleMenu: () => void;
    expanded: boolean;
    onToggleExpanded: () => void;
    showPreferences: boolean;
    onTogglePreferences: () => void;
}

/**
 * A component that encapsulates the rendering of the chat UI for the Guide
 * so that it can be presented in different contexts.
 */
export const GuideChatWindow = ({
    mediaSize,
    isCoeditorOpen,
    onClose,
    showMenu,
    onToggleMenu,
    expanded,
    onToggleExpanded,
    showPreferences,
    onTogglePreferences,
}: Props): React.ReactElement => {
    const shouldRenderSmallScreenChat = useMediaQuery("(max-width: 719px)");
    const attributes = useUserAttributes();
    const threadId = useGuideState("threadId");
    const {pathname} = useLocation();
    const chatDisabled = isGuideChatDisabled(pathname);
    const resetConversation = useResetConversation();
    const conversation = useGuideState("conversation");
    const isLargeScreen = useMediaQuery("(min-width: 1200px)");

    // Dev menu items
    const hasAccessToAIGuideDev = attributes?.hasAccessToAIGuideDev;
    const showDevControls = !!hasAccessToAIGuideDev && !chatDisabled;

    // Note that showDevControls controls whether the history link is shown,
    // and if the link is shown it can be either enabled or disabled.
    const threadHistoryEnabled = conversation.length > 1;
    const threadHistoryURL = `/profile/me/khanmigo/history/${threadId ?? ""}`;

    const headerMenuProps = {
        menuOpen: showMenu,
        onToggleMenu,
        showDevControls,
        threadHistoryEnabled,
        onResetChat: () => {
            // We don't initialize the guide when it's open
            // because we already have a useEffect that
            // does that whenever the guide is opened &&
            // initialized is false.
            resetConversation(false);
        },
        onSelectPreferences: onTogglePreferences,
        onOpenThreadHistory: () => {
            const newWindow = window.open(threadHistoryURL, "_blank");
            if (newWindow) {
                newWindow.focus();
            }
        },
        onDownloadThread: () => downloadConversation(threadId, conversation),
    };
    // We use small screen header props if we're on small viewport.
    const headerProps = shouldRenderSmallScreenChat
        ? ({
              type: HeaderType.SmallScreenChat,
              onClose,
              ...headerMenuProps,
          } as const)
        : ({
              type: HeaderType.DesktopChat,
              expanded: isLargeScreen ? expanded : false,
              expandDisabled: !isLargeScreen || isCoeditorOpen,
              onToggleExpanded,
              onClose,
              ...headerMenuProps,
          } as const);

    return (
        <>
            <View style={styles.header}>
                <Header {...headerProps} />
            </View>
            <View style={styles.coeditor}>
                <CoeditorDrawer mediaSize={mediaSize} />
            </View>
            <View style={[styles.chat, styles.fullHeight]}>
                <View style={[styles.fullHeight, styles.chatInner]}>
                    <GuideDisclaimer />
                    <Guide
                        showPreferences={showPreferences}
                        expanded={
                            isLargeScreen ? expanded && !isCoeditorOpen : false
                        }
                        onClosePreferences={onTogglePreferences}
                    />
                </View>
                <ConversationRecordedNotice
                    // Need an inline style here to take precedence
                    style={{
                        backgroundColor:
                            semanticColor.core.background.base.subtle,
                    }}
                />
            </View>
        </>
    );
};

const styles = StyleSheet.create({
    header: {
        gridArea: "header",
        flexShrink: 0,
    },
    chat: {
        gridArea: "chat",
    },
    fullHeight: {
        height: "100%",
    },
    chatInner: {
        marginLeft: 16,
        marginRight: 16,
        border: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        borderRadius: 8,
        backgroundColor: semanticColor.core.background.base.default,
        "@media screen and (max-width: 719px)": {
            marginLeft: 0,
            marginRight: 0,
            borderRadius: 0,
            border: "none",
        },
    } as any,
    coeditor: {
        gridArea: "coeditor",
    },
});
